import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";

interface StatCardProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly icon: LucideIcon;
  readonly className?: string;
  /**
   * Makes the tile a one-tap filter: it renders as a `<button>` and reports
   * its state via `aria-pressed` instead of a static, read-only `<div>`.
   */
  readonly onClick?: () => void;
  /** Whether this tile's filter is currently applied. Only meaningful together with `onClick`. */
  readonly selected?: boolean;
}

/**
 * Summary tile: an uppercase label, a large value, and an icon. Presentational
 * by default; passing `onClick` turns it into a one-tap filter button that
 * reports its selected state via `aria-pressed`.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  onClick,
  selected = false,
  className,
}: StatCardProps) {
  const isInteractive = onClick !== undefined;
  const isSelected = isInteractive && selected;

  const body = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <span
          aria-hidden="true"
          className={cn(
            "grid size-7 place-items-center rounded-lg",
            isSelected
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground",
          )}
        >
          <Icon className="size-4" />
        </span>
      </div>
      <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">
        {value}
      </p>
    </>
  );

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={isSelected}
        className={cn(
          "rounded-2xl border bg-surface p-4 text-left shadow-xs transition-colors",
          isSelected
            ? "border-primary/40 ring-1 ring-primary/30"
            : "border-border/70 hover:border-border",
          className,
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      className={cn(
        "rounded-2xl border border-border/70 bg-surface p-4 shadow-xs",
        className,
      )}
    >
      {body}
    </div>
  );
}
